import type { SvgIconComponent } from "@mui/icons-material";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Caption } from "../../../components/primitives/data-display/Caption";
import { Pill } from "../../../components/primitives/data-display/Pill";
import { gaps } from "../../../components/primitives/spacing";
import { styled } from "../../../lib/styled";
import type { CorpusScanBadge, CorpusScanHeading } from "./corpus_scan_contract";

const CardSurface = styled(ButtonBase)<{ $selected: boolean }>(({ theme, $selected }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  justifyContent: "flex-start",
  textAlign: "left",
  width: "100%",
  height: "100%",
  minHeight: theme.spacing(14),
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${$selected ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  "&:hover": { borderColor: theme.palette.primary.main },
}));

interface CorpusScanCardProps extends CorpusScanHeading {
  Icon: SvgIconComponent;
  selected: boolean;
  /** What the last run found; null when the scan has not run. */
  badge: CorpusScanBadge | null;
  onSelect: () => void;
}

/** One scan in the grid of scans: all the same size, the selected one outlined, a badge saying what
 *  its last run found, an icon beside the title. */
export function CorpusScanCard({
  title,
  description,
  Icon,
  selected,
  badge,
  onSelect,
}: CorpusScanCardProps) {
  return (
    <CardSurface $selected={selected} aria-pressed={selected} onClick={onSelect}>
      <Stack gap={gaps.tight} flexGrow={1}>
        <Stack direction="row" gap={gaps.snug} alignItems="center">
          <Icon color={selected ? "primary" : "action"} />
          <Typography variant="subtitle1" component="h2">
            {title}
          </Typography>
        </Stack>
        <Caption>{description}</Caption>
      </Stack>
      {badge ? (
        <Stack alignItems="flex-start" mt={gaps.snug}>
          <Pill label={badge.label} tone={badge.tone === "attention" ? "warning" : "muted"} />
        </Stack>
      ) : null}
    </CardSurface>
  );
}
